import {
    Fragment,
    FunctionComponent,
    useCallback,
    useEffect,
    useState,
} from 'react';
import {
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    Drawer,
    Stack,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material';
import { CardListRow } from '@/components/Card/CardListRow';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@heroicons/react/24/solid/PencilIcon';
import { CouponSearch } from '@/pageContents/couponsContent/CouponSearch';
import { debounce } from 'lodash';
import { useGetCouponsQuery } from '@/api/v2/coupon';
import {
    Coupon,
    CreateCoupon,
    createNewCoupon,
    isOfTypeCoupon,
} from '@/models/coupon';
import { CreateCouponDrawerContent } from '@/pageContents/couponsContent/CouponDrawerContent';
import { CouponEditDrawerContent } from '@/pageContents/couponsContent/CouponEditDrawerContent';

export const CouponPageContent: FunctionComponent = () => {
    const theme = useTheme();
    const { data: coupons } = useGetCouponsQuery();
    const [drawerConfig, setDrawerConfig] = useState<{
        isOpen: boolean;
        coupon: Coupon | CreateCoupon;
    }>({ isOpen: false, coupon: null });
    const [filteredCoupons, setFilteredCoupons] = useState<Coupon[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const onSearchChange = useCallback(
        debounce((value) => {
            setSearchTerm(value);
        }, 250),
        []
    );

    const onCloseDrawer = () => {
        setDrawerConfig((prevState) => ({
            ...prevState,
            isOpen: false,
        }));
    };

    useEffect(() => {
        if (coupons) {
            if (searchTerm.length > 3) {
                setFilteredCoupons(() =>
                    coupons.filter((coupon) => coupon.name.includes(searchTerm))
                );
            } else {
                setFilteredCoupons(coupons);
            }
        }
    }, [coupons]);

    useEffect(() => {
        if (searchTerm.length) {
            setFilteredCoupons(() =>
                coupons.filter((coupon) =>
                    coupon.name
                        .toLocaleLowerCase()
                        .includes(searchTerm.toLocaleLowerCase())
                )
            );
        } else {
            setFilteredCoupons(coupons || []);
        }
    }, [searchTerm]);

    return (
        <Container
            sx={{
                mt: 3,
                ml: 'unset',
            }}
            maxWidth='lg'
        >
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Coupons
            </Typography>
            <Stack spacing={theme.page.verticalSpacing}>
                <CouponSearch onChange={onSearchChange} />
                <Card>
                    <CardContent
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant='h6'>Angelegte Coupons</Typography>

                        <Button
                            sx={{
                                ml: 'auto',
                            }}
                            variant='contained'
                            onClick={() => {
                                setDrawerConfig({
                                    isOpen: true,
                                    coupon: createNewCoupon(),
                                });
                            }}
                        >
                            + Neuen Coupon anlegen
                        </Button>
                    </CardContent>
                    <Divider
                        sx={{
                            width: '100%',
                        }}
                    />

                    {filteredCoupons?.map((coupon, index) => (
                        <Fragment key={index}>
                            <CardListRow
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography>{coupon.name}</Typography>

                                <Stack
                                    sx={{
                                        ml: 'auto',
                                        alignItems: 'center',
                                    }}
                                    direction='row'
                                    spacing={2}
                                >
                                    <IconButton
                                        onClick={() => {
                                            setDrawerConfig({
                                                isOpen: true,
                                                coupon,
                                            });
                                        }}
                                    >
                                        <SvgIcon>
                                            <EditIcon />
                                        </SvgIcon>
                                    </IconButton>
                                </Stack>
                            </CardListRow>

                            {index < coupons.length - 1 && (
                                <Divider
                                    sx={{
                                        width: '100%',
                                    }}
                                />
                            )}
                        </Fragment>
                    ))}
                </Card>

                <Drawer
                    anchor='right'
                    open={drawerConfig.isOpen}
                    onClose={onCloseDrawer}
                >
                    {isOfTypeCoupon(drawerConfig.coupon) ? (
                        <CouponEditDrawerContent
                            coupon={drawerConfig.coupon as Coupon}
                            onClose={onCloseDrawer}
                        />
                    ) : (
                        <CreateCouponDrawerContent
                            coupon={drawerConfig.coupon as CreateCoupon}
                            onClose={onCloseDrawer}
                        />
                    )}
                </Drawer>
            </Stack>
        </Container>
    );
};
